import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
    type MouseEvent,
    type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import CenteredInfoLayout from '~/components/layout/templates/CenteredInfo';
import type { BigIconName } from '~/components/new/BigIcon';
import Button, { type ButtonProps } from '~/components/new/Button';
import IconAction from '~/components/new/IconAction';
import { isE2ETest } from '~/utils/env';
import type { DataTestAttributes } from '~/utils/qa';
import { useMergeRefs } from '~/utils/react';
import { classNames } from '~/utils/styles';
import { RemixIconNames } from './Icon';

const contextModal = React.createContext<(() => void) | undefined>(undefined);

export type ModalService<T = object> = T & {
    isOpen: boolean;
    open: (props?: T & DataTestAttributes) => void;
    toggle: () => void;
    close: () => void;
    closeGracefully: () => void;
};

interface OnCloser {
    onClose: () => void;
}

export function useModal<T = object>(initialValue = false): ModalService<T> {
    // old method that make the modal suddenly appear/disappear
    const [open, setOpen] = useState(initialValue);
    // new method that allows for graceful closing with transition
    const [show, setShow] = useState(initialValue);

    const [props, setProps] = useState<T>({} as T);
    const tId = useRef<NodeJS.Timeout | null>(null);

    const clearProps = useCallback(() => {
        clearTimeout(tId.current!);
        tId.current = setTimeout(
            () => {
                setProps({} as T);
            },
            isE2ETest() ? 0 : 250,
        ); // delay to allow transition to finish
    }, []);

    return useMemo(
        () => ({
            ...props,
            isOpen: open,
            show,
            open: (props = {} as T & DataTestAttributes) => {
                setProps(props);
                setOpen(true); // old method
                setShow(true);
            },
            toggle: () => {
                setOpen(!open); // old method
                setShow(!open);

                clearProps();
            },
            close: () => {
                setOpen(false); // old method
                setShow(false);
                clearProps();
                (props as OnCloser)?.onClose?.();
            },
            onClose: () => {
                setOpen(false); // old method
                setShow(false);
                clearProps();
                (props as OnCloser)?.onClose?.();
            },
            closeGracefully: () => {
                setShow(false);
                clearProps();
            },
        }),
        [props, open, show, clearProps],
    );
}

export type ModalAPI = {
    close: () => Promise<void>;
    isOpen: boolean;
};

export type ModalProps = {
    show?: boolean;
    usePortal?: boolean;
    size?: ModalSize;
    centerContent?: boolean;
    onClose?: () => void;
    onCloseRequest?: () => boolean | void;
    children: ReactNode;
    fixed?: boolean;
    className?: string;
    ref?: React.RefObject<ModalAPI>;
} & DataTestAttributes;

type ModalSize = keyof typeof COLS_BY_SIZE;

const COLS_BY_SIZE = {
    small: 6,
    medium: 8,
    large: 12,
} as const;

export default function Modal({
    show = true,
    usePortal = false,
    size = 'medium',
    centerContent = false,
    onClose,
    onCloseRequest = () => true,
    children,
    fixed = false,
    className = '',
    ref,
    'data-test-component': testComponent = 'ui/Modal',
    'data-test-name': testName,
}: ModalProps) {
    const refOverlay = useRef<HTMLDivElement>(null!);
    const [closing, setClosing] = useState(false);
    const [isOpen, setIsOpen] = useState(show);
    const { preventCloseOnClickOutside } = useContext(modalContext);

    const closeHandler = useCallback(async () => {
        if (onCloseRequest()) {
            return new Promise<void>((resolve) => {
                setClosing(true); // for transition
                setTimeout(
                    () => {
                        setIsOpen(false);
                        onClose?.();
                        resolve();
                    },
                    isE2ETest() ? 0 : 250,
                ); // transition delay
            });
        }
    }, [onClose, onCloseRequest]);

    useEffect(() => {
        if (show && !isOpen && !closing) {
            setIsOpen(true);
        }
        if (!show && isOpen && !closing) {
            void closeHandler();
        }
    }, [show, closeHandler]); // intentionally excluded isOpen and closing to avoid conflicts between internal state and external show prop

    useEffect(() => {
        if (show && !isOpen && closing) {
            setClosing(false);
            setIsOpen(true);
        }
    }, [show]); // reopen after graceful close via show prop; intentionally excluded isOpen and closing

    useEffect(() => {
        if (!isOpen) return;

        function onKeyDown(e: KeyboardEvent) {
            if (e.key !== 'Escape') return;
            e.stopPropagation();
            void closeHandler();
        }

        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [isOpen, closeHandler]);

    const cls = classNames({
        'modal-overlay': true,
        RemotelyTheme: usePortal,
        'modal-center-content': centerContent,
        'modal-fixed': fixed,
        [className]: Boolean(className),
    });

    const handleClose = (e: MouseEvent<HTMLDivElement>) => {
        // close only if clicked on overlay, not on any other element
        if (e.target === refOverlay.current && !preventCloseOnClickOutside) {
            void closeHandler();
            // prevent nested modal to cascade closing
            e.stopPropagation();
        }
    };

    useImperativeHandle(
        ref,
        () => ({
            close: closeHandler,
            isOpen: isOpen,
        }),
        [closeHandler, isOpen],
    );

    const render = usePortal ? createPortal : (x: ReactNode) => x;

    return isOpen
        ? render(
              <div
                  ref={refOverlay}
                  className={cls}
                  onMouseDown={handleClose}
                  data-test-component={testComponent}
                  data-test-name={testName}
                  role='dialog'
                  data-test-screenshot-root>
                  <div
                      className={classNames({
                          [`modal max-w-${COLS_BY_SIZE[size]}col`]: true,
                          'as-panel': fixed,
                          closing: closing,
                      })}>
                      <contextModal.Provider value={() => void closeHandler()}>
                          {children}
                      </contextModal.Provider>
                  </div>
              </div>,
              document.body,
          )
        : null;
}

type ModalTitleProps = {
    children?: ReactNode;
    className?: string;
    actions?: Pick<ButtonProps, 'icon' | 'onClick' | 'title'>[];
    rightSide?: ReactNode;
    caption?: string;
    divided?: boolean;
    addOns?: ReactNode;
    info?: string;
    'data-test-component'?: string;
    'data-test-name'?: string;
};

Modal.Header = function Header({
    children = null,
    className = '',
    actions,
    caption = '',
    divided = true,
    rightSide = null,
    addOns,
    info,
    'data-test-component': testComponent = 'ui/Modal/Header',
    'data-test-name': testName,
}: ModalTitleProps) {
    const cls = classNames({
        'modal-header': true,
        'modal-header-divided': divided,
        [className]: Boolean(className),
    });

    const onClose = useContext(contextModal);

    return (
        <div
            className={cls}
            data-test-component={testComponent}
            data-test-name={testName}>
            <div className='modal-header-title'>
                <h2
                    className={classNames({
                        'modal-title ellipsis': true,
                        'with-close-button': Boolean(onClose),
                    })}>
                    {children}
                </h2>
                <div className='modal-header-title-caption'>{caption}</div>
                {info && (
                    <IconAction
                        icon={RemixIconNames.RiInformationLine}
                        colorVar='core-tertiary'
                        title={info}
                    />
                )}
            </div>
            <div className='modal-header-addons'>{addOns}</div>
            {(onClose || actions || rightSide) && (
                <>
                    {rightSide}
                    <div
                        className='modal-header-actions'
                        data-test-component='ui/Modal/Header/Actions'>
                        {actions?.map((actionProps, idx) => (
                            <Button
                                {...actionProps}
                                key={idx}
                                variant='filled-light'
                                size='medium'
                            />
                        ))}
                        <Button
                            icon={RemixIconNames.RiCloseFill}
                            onClick={() => onClose?.()}
                            variant='floating'
                            size='medium'
                            data-test-name='close'
                        />
                    </div>
                </>
            )}
        </div>
    );
};

type ModalBodyProps = {
    children: ReactNode;
    className?: string;
    reference?: React.RefObject<HTMLDivElement>;
    'data-test-component'?: string;
    'data-test-name'?: string;
};

Modal.Body = function ModalBody({
    children,
    className = '',
    reference,
    'data-test-component': testComponent = 'ui/Modal/Body',
    'data-test-name': testName,
    ...props
}: ModalBodyProps) {
    const bodyRef = useMergeRefs(reference);

    const checkOverflow = useCallback(() => {
        if (bodyRef.current) {
            if (bodyRef.current.scrollHeight > bodyRef.current.clientHeight) {
                bodyRef.current.classList.add('modal-body-overflow');
            } else {
                bodyRef.current.classList.remove('modal-body-overflow');
            }
        }
    }, [bodyRef]);

    useEffect(() => {
        const resizeObserver = new ResizeObserver(() => requestAnimationFrame(checkOverflow));
        resizeObserver.observe(bodyRef.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    return (
        <div
            className='modal-body'
            ref={bodyRef}
            data-test-component={testComponent}
            data-test-name={testName}
            {...props}>
            <div
                className={classNames({
                    'modal-body-content-wrapper': true,
                    [className]: Boolean(className),
                })}>
                {children}
            </div>
        </div>
    );
};

type ModalFooterProps = {
    children: ReactNode;
    align?: 'left' | 'right';
    className?: string;
    'data-test-component'?: string;
    'data-test-name'?: string;
};

Modal.Footer = ({
    children,
    align = 'left',
    className = '',
    'data-test-component': testComponent = 'ui/Modal/Footer',
    'data-test-name': testName,
    ...props
}: ModalFooterProps) => (
    <div
        className={classNames({
            'modal-footer modal-footer-divided': true,
            [`modal-footer-${align}`]: true,
            [className]: Boolean(className),
        })}
        data-test-component={testComponent}
        data-test-name={testName}
        {...props}>
        {children}
    </div>
);

type ModalBigIconContentProps = {
    icon?: BigIconName;
    title: string;
    description?: ReactNode;
    children?: ReactNode;
    'data-test-name'?: string;
};

Modal.BigIconContent = ({
    icon = 'info',
    title,
    description,
    children,
    'data-test-name': testName,
}: ModalBigIconContentProps) => (
    <div className='flex flex-column items-center desktop-body-1-regular'>
        <CenteredInfoLayout.Header
            icon={icon}
            title={title}
            subtitle={description}
            data-test-name={testName}
        />
        {children}
    </div>
);

export type ModalContextType = {
    preventCloseOnClickOutside?: boolean;
    invalidateCloseOnClickOutside: (val: boolean) => void;
};

export const modalContext = createContext<ModalContextType>({
    preventCloseOnClickOutside: false,
    invalidateCloseOnClickOutside: () => {},
});

export function ModalProvider({ children }: { children: ReactNode }) {
    const [preventCloseOnClickOutside, invalidateCloseOnClickOutside] = useState(false);
    return (
        <modalContext.Provider
            value={{ preventCloseOnClickOutside, invalidateCloseOnClickOutside }}>
            {children}
        </modalContext.Provider>
    );
}
